import React from "react";
import { Button, Modal, ModalFooter } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { GeneralDashboardComplex } from "../../api/generalDashboard/generalDashboard.types";
import {
  GeneralDashboardModalDetailField,
  GeneralDashboardModalSection,
} from "../GeneralDashboardModalFields";

interface GeneralComplexDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: GeneralDashboardComplex | null;
}

export const GeneralComplexDetailsModal: React.FC<GeneralComplexDetailsModalProps> = ({
  open,
  onClose,
  data,
}) => {
  const { translate } = useLocalization();
  const fallback = translate("admin.complexes.details.notProvided");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translate("general.dashboard.complex.modalTitle")}
      content={
        <div className="general-dashboard-modal-body general-dashboard-modal-body--narrow">
          <GeneralDashboardModalSection
            title={translate("admin.complexes.details.section.identification")}
          >
            <div className="general-dashboard-modal-field-grid">
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.name")}
                value={data?.name}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.fantasyName")}
                value={data?.fantasyName}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.cnpj")}
                value={data?.cnpj}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.municipalRegistration")}
                value={data?.municipalRegistration}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.complexPhone")}
                value={data?.phone}
                fallback={fallback}
              />
            </div>
          </GeneralDashboardModalSection>

          <GeneralDashboardModalSection
            title={translate("admin.complexes.details.section.contact")}
          >
            {data?.responsible ? (
              <div className="general-dashboard-modal-field-grid">
                <GeneralDashboardModalDetailField
                  label={translate("admin.complexes.details.label.responsibleName")}
                  value={data.responsible.name}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("management.clients.form.label.responsibleEmail")}
                  value={data.responsible.email}
                  fallback={fallback}
                />
                <GeneralDashboardModalDetailField
                  label={translate("admin.complexes.details.label.responsiblePhone")}
                  value={data.responsible.phone}
                  fallback={fallback}
                />
              </div>
            ) : (
              <p className="text-sm opacity-70">
                {translate("admin.complexes.details.empty")}
              </p>
            )}
          </GeneralDashboardModalSection>

          <GeneralDashboardModalSection
            title={translate("admin.complexes.details.section.address")}
          >
            <div className="general-dashboard-modal-field-grid">
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.zipCode")}
                value={data?.address.zipCode}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.street")}
                value={data?.address.street}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.number")}
                value={data?.address.number}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.complement")}
                value={data?.address.complement}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.neighborhood")}
                value={data?.address.neighborhood}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.city")}
                value={data?.address.city}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.state")}
                value={data?.address.state}
                fallback={fallback}
              />
              <GeneralDashboardModalDetailField
                label={translate("admin.complexes.details.label.country")}
                value={data?.address.country}
                fallback={fallback}
              />
            </div>
          </GeneralDashboardModalSection>

          <ModalFooter
            footer={
              <Button type="button" variant="primary" onClick={onClose}>
                {translate("admin.complexes.button.close")}
              </Button>
            }
          />
        </div>
      }
    />
  );
};
